import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import MoodOrb from '../components/MoodOrb';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trash2, MapPin, Music, Calendar, Play, Pause, Mic } from 'lucide-react';
import { toast } from 'sonner';

const EntryDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { api } = useAuth();
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        fetchEntry();
    }, [id]);

    const fetchEntry = async () => {
        try {
            const response = await api.get(`/journal/entries/${id}`);
            setEntry(response.data);
        } catch (error) {
            console.error('Failed to fetch entry:', error);
            toast.error('Entry not found');
            navigate('/entries');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this entry?')) return;
        
        try {
            await api.delete(`/journal/entries/${id}`);
            toast.success('Entry deleted');
            navigate('/entries');
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const togglePlayback = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-pulse text-muted-foreground">Loading entry...</div>
                </div>
            </DashboardLayout>
        );
    }

    if (!entry) return null;

    return (
        <DashboardLayout>
            <div data-testid="entry-detail-page" className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <Link to="/entries">
                        <Button variant="ghost" className="rounded-full">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Entries
                        </Button>
                    </Link>
                    <Button
                        variant="outline"
                        onClick={handleDelete}
                        className="rounded-full text-destructive hover:bg-destructive/10"
                        data-testid="delete-entry-btn"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                    </Button>
                </div>

                {/* Entry Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Card 
                        className="glass-card overflow-hidden"
                        style={{
                            backgroundColor: entry.custom_colors?.background || undefined,
                            color: entry.text_color || undefined
                        }}
                    >
                        <CardHeader className="pb-4">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-4">
                                    <span className="text-5xl">{entry.emoji}</span>
                                    <div>
                                        <CardTitle className="font-heading text-2xl">
                                            Mood: {entry.mood_rating}/10
                                        </CardTitle>
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                {new Date(entry.created_at).toLocaleDateString('en-US', {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: 'numeric',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <MoodOrb moodRating={entry.mood_rating} emoji={entry.emoji} size="sm" />
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Meta Info */}
                            <div className="flex flex-wrap gap-4">
                                {entry.entry_type === 'voice' && (
                                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10">
                                        <Mic className="w-4 h-4 text-primary" />
                                        <span className="text-primary font-medium">Voice Entry</span>
                                    </div>
                                )}
                                {entry.song_or_genre && (
                                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
                                        <Music className="w-4 h-4" />
                                        <span>{entry.song_or_genre}</span>
                                    </div>
                                )}
                                {entry.location_name && (
                                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
                                        <MapPin className="w-4 h-4" />
                                        <span>{entry.location_name}</span>
                                    </div>
                                )}
                            </div>

                            {/* Voice Entry Player */}
                            {entry.entry_type === 'voice' && entry.audio_data && (
                                <div className="p-6 rounded-2xl bg-muted/30">
                                    <audio
                                        ref={audioRef}
                                        src={entry.audio_data}
                                        onEnded={() => setIsPlaying(false)}
                                        className="hidden"
                                    />
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={togglePlayback}
                                            className="w-14 h-14 rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 transition-colors"
                                            data-testid="play-voice-entry"
                                        >
                                            {isPlaying ? (
                                                <Pause className="w-6 h-6 text-white" />
                                            ) : (
                                                <Play className="w-6 h-6 text-white ml-1" />
                                            )}
                                        </button>
                                        <div>
                                            <p className="font-medium">Voice Recording</p>
                                            {entry.audio_duration && (
                                                <p className="text-sm text-muted-foreground">
                                                    Duration: {formatDuration(entry.audio_duration)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Text Content */}
                            {entry.content && (
                                <div className="prose prose-lg max-w-none dark:prose-invert">
                                    <p className="whitespace-pre-wrap text-lg leading-relaxed">
                                        {entry.content}
                                    </p>
                                </div>
                            )}

                            {/* Stickers */}
                            {entry.stickers && entry.stickers.length > 0 && (
                                <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                                    {entry.stickers.map((sticker, index) => (
                                        <span key={index} className="text-2xl" title={sticker}>
                                            {sticker === 'heart' && '❤️'}
                                            {sticker === 'star' && '⭐'}
                                            {sticker === 'sun' && '☀️'}
                                            {sticker === 'moon' && '🌙'}
                                            {sticker === 'rainbow' && '🌈'}
                                            {sticker === 'flower' && '🌸'}
                                            {sticker === 'butterfly' && '🦋'}
                                            {sticker === 'sparkle' && '✨'}
                                            {sticker === 'cloud' && '☁️'}
                                            {sticker === 'leaf' && '🍃'}
                                            {sticker === 'fire' && '🔥'}
                                            {sticker === 'water' && '💧'}
                                            {sticker === 'peace' && '☮️'}
                                            {sticker === 'pray' && '🙏'}
                                            {sticker === 'muscle' && '💪'}
                                            {sticker === 'brain' && '🧠'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </DashboardLayout>
    );
};

export default EntryDetailPage;
